/**
 * Adaptateurs pour transformer les réponses du backend vers le format attendu par le frontend
 * 
 * Le backend retourne une structure avec:
 * - fiscalYear: string
 * - company: { name, logo, rccm, idnat, nif, creditRating, esgCode, financialScore, audited, ... }
 * - reportData: { balanceSheet | revenue/expenses/netIncome | operatingActivities/... | equityComponents }
 * 
 * Après passage par ApiService, on reçoit directement cette structure (sans wrapper 'data')
 * Le frontend attend une structure plate directement utilisable par les composants UI
 */

import type { 
  BalanceSheetData, 
  IncomeStatementData, 
  CashFlowStatementData,
  EquityChangesData,
  FinancialItem
} from '../types/reports';

// Interface pour la réponse brute du backend (après transformation ApiService)
// Correspond à apiResponse.data qui contient { fiscalYear, company, reportData }
export interface BackendReportData<T = any> {
  fiscalYear: string;
  company: {
    name: string;
    logo?: string;
    date?: string | null;
    rccm?: string;
    idnat?: string;
    nif?: string;
    creditRating?: string;
    esgCode?: string;
    financialScore?: string;
    audited?: boolean;
    auditorCode?: string;
    generatedDate?: string;
    generatedBy?: string;
    role?: string;
  };
  reportData: T;
  period?: {
    startDate?: string;
    endDate?: string;
  };
  currency?: string;
}

// Interface pour les métadonnées extraites
export interface ReportMetadata {
  fiscalYear: string;
  company: BackendReportResponse['data']['company'];
  generatedDate?: string;
  currency?: string;
}

// ==================== BALANCE SHEET ADAPTER ====================

interface BackendBalanceSheetData {
  balanceSheet: {
    fixedAssets: {
      intangibleAssets: FinancialItem[];
      tangibleAssets: FinancialItem[];
      financialAssets?: FinancialItem[];
      total: FinancialItem;
    };
    currentAssets: {
      inventory: FinancialItem[];
      receivables?: FinancialItem[];
      other?: FinancialItem[];
      total: FinancialItem;
    };
    treasuryAssets?: {
      investments?: FinancialItem[];
      banks?: FinancialItem[];
      cashOnHand?: FinancialItem[];
      total: FinancialItem;
    };
    grandTotal: FinancialItem;
    // Passif (si présent)
    equity?: {
      capital?: FinancialItem[];
      reserves?: FinancialItem[];
      result?: FinancialItem[];
      other?: FinancialItem[];
      total: FinancialItem;
    };
    financialDebts?: {
      loans?: FinancialItem[];
      leasing?: FinancialItem[];
      other?: FinancialItem[];
      total: FinancialItem;
    };
    currentLiabilities?: {
      suppliers?: FinancialItem[];
      fiscal?: FinancialItem[];
      social?: FinancialItem[];
      other?: FinancialItem[];
      total: FinancialItem;
    };
    treasuryLiabilities?: {
      banks?: FinancialItem[];
      other?: FinancialItem[];
      total: FinancialItem;
    };
  };
}

/**
 * Transforme la réponse du backend pour le bilan vers le format frontend
 * @param response - La réponse du backend (déjà unwrapped par ApiService)
 */
export function adaptBalanceSheetResponse(
  response: BackendReportData<BackendBalanceSheetData>
): { data: BalanceSheetData; metadata: ReportMetadata } {
  const { fiscalYear, company, reportData } = response;
  
  // Créer un item financier vide par défaut
  const emptyItem: FinancialItem = { code: '', label: '', brut: 0, amort: 0, net: 0, netN1: 0 };
  const emptyTotal: FinancialItem = { code: 'T', label: 'Total', brut: 0, amort: 0, net: 0, netN1: 0 };
  
  // Gérer les différentes structures possibles de réponse
  // Structure 1: reportData.balanceSheet (standard)
  // Structure 2: reportData contient directement les données du bilan
  const bs = reportData?.balanceSheet || reportData || {};
  
  console.log('📊 Balance sheet adapter - structure:', { 
    hasBalanceSheet: !!reportData?.balanceSheet,
    bsKeys: bs ? Object.keys(bs) : null
  });

  const data: BalanceSheetData = {
    // Actif
    fixedAssets: {
      intangibleAssets: bs.fixedAssets?.intangibleAssets || [],
      tangibleAssets: bs.fixedAssets?.tangibleAssets || [],
      financialAssets: bs.fixedAssets?.financialAssets || [],
      total: bs.fixedAssets?.total || emptyTotal
    },
    currentAssets: {
      inventory: bs.currentAssets?.inventory || [],
      receivables: bs.currentAssets?.receivables || [],
      other: bs.currentAssets?.other || [],
      total: bs.currentAssets?.total || emptyTotal
    },
    treasuryAssets: {
      investments: bs.treasuryAssets?.investments || [],
      banks: bs.treasuryAssets?.banks || [],
      cashOnHand: bs.treasuryAssets?.cashOnHand || [],
      total: bs.treasuryAssets?.total || emptyTotal
    },
    totalAssets: bs.grandTotal || bs.totalAssets || emptyTotal,

    // Passif (avec valeurs par défaut si non présent)
    equity: {
      capital: bs.equity?.capital || [],
      reserves: bs.equity?.reserves || [],
      result: bs.equity?.result || [],
      other: bs.equity?.other || [],
      total: bs.equity?.total || emptyTotal
    },
    financialDebts: {
      loans: bs.financialDebts?.loans || [],
      leasing: bs.financialDebts?.leasing || [],
      other: bs.financialDebts?.other || [],
      total: bs.financialDebts?.total || emptyTotal
    },
    currentLiabilities: {
      suppliers: bs.currentLiabilities?.suppliers || [],
      fiscal: bs.currentLiabilities?.fiscal || [],
      social: bs.currentLiabilities?.social || [],
      other: bs.currentLiabilities?.other || [],
      total: bs.currentLiabilities?.total || emptyTotal
    },
    treasuryLiabilities: {
      banks: bs.treasuryLiabilities?.banks || [],
      other: bs.treasuryLiabilities?.other || [],
      total: bs.treasuryLiabilities?.total || emptyTotal
    },
    totalLiabilities: bs.grandTotal || bs.totalLiabilities || emptyTotal
  };

  return {
    data,
    metadata: {
      fiscalYear,
      company,
      generatedDate: company?.generatedDate
    }
  };
}

// ==================== INCOME STATEMENT ADAPTER ====================

interface BackendIncomeStatementData {
  revenue: {
    sales: FinancialItem;
    production?: FinancialItem;
    other?: FinancialItem;
    total: FinancialItem;
  };
  expenses: {
    cogs: FinancialItem;
    services?: FinancialItem;
    personnel?: FinancialItem;
    depreciation?: FinancialItem;
    other?: FinancialItem;
    total: FinancialItem;
  };
  operatingResult?: FinancialItem;
  financialResult?: FinancialItem;
  netIncome: FinancialItem;
}

/**
 * Transforme la réponse du backend pour le compte de résultat vers le format frontend
 * @param response - La réponse du backend (déjà unwrapped par ApiService)
 */
export function adaptIncomeStatementResponse(
  response: BackendReportData<BackendIncomeStatementData>
): { data: IncomeStatementData; metadata: ReportMetadata } {
  const { fiscalYear, company, reportData } = response;
  const emptyItem: FinancialItem = { code: '', label: '', current: 0, previous: 0 };
  const emptyTotal: FinancialItem = { code: 'T', label: 'Total', current: 0, previous: 0 };

  // Convertir les items simples en tableaux pour le frontend
  const toArray = (item: FinancialItem | undefined): FinancialItem[] => 
    item ? [item] : [];

  // Gérer les différentes structures possibles
  const incomeData = reportData || {};
  const revenue = incomeData.revenue || { sales: emptyItem, total: emptyTotal };
  const expenses = incomeData.expenses || { cogs: emptyItem, total: emptyTotal };
  
  console.log('📊 Income statement adapter - structure:', { 
    hasRevenue: !!incomeData.revenue,
    hasExpenses: !!incomeData.expenses,
    revenueKeys: revenue ? Object.keys(revenue) : null
  });

  // Calculer le résultat d'exploitation si non fourni
  const operatingResult = incomeData.operatingResult || {
    code: 'RE',
    label: 'Résultat d\'exploitation',
    current: (revenue.total?.current || 0) - (expenses.total?.current || 0),
    previous: (revenue.total?.previous || 0) - (expenses.total?.previous || 0)
  };

  const data: IncomeStatementData = {
    operatingIncome: {
      sales: toArray(revenue.sales),
      production: toArray(revenue.production),
      grants: [],
      other: toArray(revenue.other),
      total: revenue.total || emptyTotal
    },
    operatingExpenses: {
      purchases: toArray(expenses.cogs),
      services: toArray(expenses.services),
      taxes: [],
      personnel: toArray(expenses.personnel),
      depreciation: toArray(expenses.depreciation),
      other: toArray(expenses.other),
      total: expenses.total || emptyTotal
    },
    operatingResult,
    financialIncome: [],
    financialExpenses: [],
    financialResult: incomeData.financialResult || emptyItem,
    ordinaryResult: {
      code: 'RAO',
      label: 'Résultat des activités ordinaires',
      current: operatingResult.current || 0,
      previous: operatingResult.previous || 0
    },
    extraordinaryIncome: [],
    extraordinaryExpenses: [],
    extraordinaryResult: emptyItem,
    taxes: emptyItem,
    netResult: incomeData.netIncome || emptyItem
  };

  return {
    data,
    metadata: { fiscalYear, company }
  };
}

// ==================== CASH FLOW ADAPTER ====================

interface BackendCashFlowData {
  operatingActivities: {
    netIncome: FinancialItem;
    adjustments: FinancialItem[];
    total: FinancialItem;
  };
  investingActivities: {
    items?: FinancialItem[];
    total: FinancialItem;
  };
  financingActivities: {
    items?: FinancialItem[];
    total: FinancialItem;
  };
  netCashFlow: FinancialItem;
  openingCash?: FinancialItem;
  closingCash?: FinancialItem;
}

/**
 * Transforme la réponse du backend pour le tableau de flux de trésorerie
 * @param response - La réponse du backend (déjà unwrapped par ApiService)
 */
export function adaptCashFlowResponse(
  response: BackendReportData<BackendCashFlowData>
): { data: CashFlowStatementData; metadata: ReportMetadata } {
  const { fiscalYear, company, reportData } = response;
  const emptyItem: FinancialItem = { code: '', label: '', current: 0, previous: 0 };
  const emptyTotal: FinancialItem = { code: 'T', label: 'Total', current: 0, previous: 0 };

  // Gérer les différentes structures possibles
  const cashFlowData = reportData || {};
  const operatingActivities = cashFlowData.operatingActivities || { netIncome: emptyItem, adjustments: [], total: emptyTotal };
  const investingActivities = cashFlowData.investingActivities || { total: emptyTotal };
  const financingActivities = cashFlowData.financingActivities || { total: emptyTotal };

  console.log('📊 Cash flow adapter - structure:', { 
    hasOperatingActivities: !!cashFlowData.operatingActivities,
    hasInvestingActivities: !!cashFlowData.investingActivities,
    hasFinancingActivities: !!cashFlowData.financingActivities
  });

  // Séparer les ajustements en catégories
  const adjustments = operatingActivities.adjustments || [];
  const workingCapitalItems = adjustments.filter((a: any) => 
    a.code === 'CWC' || a.label?.toLowerCase().includes('bfr') || a.label?.toLowerCase().includes('working')
  );
  const otherAdjustments = adjustments.filter((a: any) => 
    a.code !== 'CWC' && !a.label?.toLowerCase().includes('bfr') && !a.label?.toLowerCase().includes('working')
  );

  // Convertir 'amount' en 'current' pour la compatibilité avec le frontend
  const convertAmountToCurrent = (item: any): FinancialItem => ({
    code: item?.code || '',
    label: item?.label || '',
    current: item?.amount ?? item?.current ?? 0,
    previous: item?.previous ?? 0
  });

  const data: CashFlowStatementData = {
    operatingActivities: {
      netResult: convertAmountToCurrent(operatingActivities.netIncome),
      adjustments: otherAdjustments.map(convertAmountToCurrent),
      workingCapital: workingCapitalItems.map(convertAmountToCurrent),
      total: convertAmountToCurrent(operatingActivities.total)
    },
    investingActivities: {
      acquisitions: [],
      disposals: [],
      total: convertAmountToCurrent(investingActivities.total)
    },
    financingActivities: {
      capital: [],
      loans: [],
      repayments: [],
      dividends: [],
      total: convertAmountToCurrent(financingActivities.total)
    },
    netCashChange: convertAmountToCurrent(cashFlowData.netCashFlow || emptyItem),
    openingCash: cashFlowData.openingCash ? convertAmountToCurrent(cashFlowData.openingCash) : emptyItem,
    closingCash: cashFlowData.closingCash ? convertAmountToCurrent(cashFlowData.closingCash) : emptyItem
  };

  return {
    data,
    metadata: { fiscalYear, company }
  };
}

// ==================== EQUITY CHANGES ADAPTER ====================

interface BackendEquityChangesData {
  period?: { startDate?: string; endDate?: string };
  currency?: string;
  equityComponents: Array<{
    code: string;
    label: string;
    openingBalance: number;
    changes: number;
    closingBalance: number;
  }>;
  totalEquity: {
    openingBalance: number;
    changes: number;
    closingBalance: number;
  };
}

/**
 * Transforme la réponse du backend pour le tableau de variation des capitaux propres
 * @param response - La réponse du backend (déjà unwrapped par ApiService)
 */
export function adaptEquityChangesResponse(
  response: BackendEquityChangesData
): { data: EquityChangesData; metadata: Partial<ReportMetadata> } {
  const { equityComponents = [], totalEquity, currency } = response || {};

  console.log('📊 Equity changes adapter - structure:', { 
    hasEquityComponents: !!equityComponents,
    componentsCount: equityComponents?.length || 0,
    hasTotalEquity: !!totalEquity
  });

  // Convertir les composants en format FinancialItem
  const convertComponent = (comp: any): FinancialItem => ({
    code: comp?.code || '',
    label: comp?.label || '',
    net: comp?.closingBalance ?? 0,
    netN1: comp?.openingBalance ?? 0,
    variation: comp?.changes ?? 0
  });

  // Séparer les composants par type
  const findByCode = (code: string) => 
    (equityComponents || []).filter((c: any) => c?.code?.toLowerCase().includes(code)).map(convertComponent);

  // Valeurs par défaut pour totalEquity
  const defaultTotal = { openingBalance: 0, changes: 0, closingBalance: 0 };
  const total = totalEquity || defaultTotal;

  const data: EquityChangesData = {
    capital: findByCode('capital'),
    premiums: findByCode('premium'),
    revaluationReserves: findByCode('revaluation'),
    legalReserves: findByCode('legal'),
    otherReserves: findByCode('reserves').filter((r: any) => !r.code?.includes('legal')),
    retainedEarnings: findByCode('retained'),
    netResult: findByCode('result'),
    total: {
      code: 'TOTAL',
      label: 'Total des capitaux propres',
      net: total.closingBalance ?? 0,
      netN1: total.openingBalance ?? 0,
      variation: total.changes ?? 0
    }
  };

  return {
    data,
    metadata: { currency }
  };
}

// ==================== GENERIC ADAPTER ====================

/**
 * Détecte le type de rapport et applique l'adaptateur approprié
 * @param response - La réponse du backend (apiResponse.data, déjà unwrapped)
 * @param reportType - Le type de rapport (balance, income, cashflow, etc.)
 */
export function adaptReportResponse(
  response: any,
  reportType: string
): { data: any; metadata: Partial<ReportMetadata> } {
  console.log('📊 adaptReportResponse called with:', { 
    reportType, 
    responseKeys: response ? Object.keys(response) : null,
    hasReportData: !!response?.reportData,
    hasEquityComponents: !!response?.equityComponents
  });
  
  // Si pas de réponse, retourner un objet vide
  if (!response) {
    console.warn('⚠️ No response provided to adapter');
    return { data: null, metadata: {} };
  }

  // Extraire les métadonnées communes
  const metadata: Partial<ReportMetadata> = {
    fiscalYear: response.fiscalYear,
    company: response.company,
    generatedDate: response.company?.generatedDate,
    currency: response.currency
  };

  try {
    // Pour equity-changes, la structure est différente (pas de reportData wrapper)
    if (reportType === 'equity-changes') {
      console.log('📊 Adapting equity changes...');
      if (response.equityComponents) {
        return adaptEquityChangesResponse(response);
      } else if (response.reportData?.equityComponents) {
        return adaptEquityChangesResponse(response.reportData);
      }
      // Si la structure n'est pas reconnue, retourner tel quel
      console.warn('⚠️ Equity changes structure not recognized, returning as-is');
      return { data: response, metadata };
    }

    // Pour les autres types, vérifier si reportData existe
    if (!response.reportData) {
      // Si pas de reportData, peut-être que les données sont directement dans la réponse
      // (structure plate comme le frontend l'attend)
      console.log('📊 No reportData wrapper, checking if data is already in expected format');
      
      // Vérifier si c'est déjà au format attendu
      if (response.fixedAssets || response.currentAssets || response.operatingIncome || 
          response.operatingActivities || response.capital) {
        console.log('📊 Data already in frontend format, returning as-is');
        return { data: response, metadata };
      }
      
      console.warn('⚠️ Response does not have expected structure');
      return { data: response, metadata };
    }

    switch (reportType) {
      case 'balance':
        console.log('📊 Adapting balance sheet...');
        return adaptBalanceSheetResponse(response);
      case 'income':
        console.log('📊 Adapting income statement...');
        return adaptIncomeStatementResponse(response);
      case 'cashflow':
        console.log('📊 Adapting cash flow...');
        return adaptCashFlowResponse(response);
      default:
        // Pour les autres types, extraire reportData directement
        console.log('📊 Using default adapter for type:', reportType);
        return {
          data: response.reportData,
          metadata
        };
    }
  } catch (error) {
    console.error('❌ Error adapting report:', error);
    // En cas d'erreur, retourner les données brutes
    return { 
      data: response.reportData || response, 
      metadata 
    };
  }
}
